/**
 * v2 API contract of quick capture «Зафиксировать» and of the gap fixes (docs/research/
 * design2-shell-capture.md §3–4, decisions-log V28, V29). Amounts travel in stored units: rates
 * `rateE4`, amounts minor units, hours minutes.
 */
import { z } from 'zod';
import { isIsoDate } from '../dates';
import { CURRENCIES } from '../money';
import type { PayFormat } from '../calc/prices';
import type { Gap } from './gaps';
import { SEVERANCE_OPTIONS } from './entities';

export const CAPTURE_EVENT_TYPES = [
  'join',
  'leave',
  'project',
  'rate',
  'person',
  'status',
  'payment',
  'note',
  'dismiss',
  'client',
  'employ',
  'absence',
  'edit',
  'extra',
] as const;
export type CaptureType = (typeof CAPTURE_EVENT_TYPES)[number];

/** Plan of an assignment captured without one: 168 h. */
export const DEFAULT_PLAN_MINUTES = 168 * 60;
/** Prefix of a combo value that creates the record («Создать «…»»). */
export const NEW_REF_PREFIX = 'new:';

const isoDate = z.string().refine(isIsoDate, 'Дата в формате ГГГГ-ММ-ДД');
const id = z.string().min(1);
const currency = z.enum(CURRENCIES);
const positiveInt = z.number().int().positive();

/** Percent × 10 000 (`shareE6`): 1 000 000 = 100 %. */
const MAX_SHARE_E6 = 1_000_000;

/**
 * A rate (`rateE4`, hourly), an amount (`amountMinor`: per month, or the scope total), a share of
 * the project's revenue or profit (`shareE6`) or the salary through an entity (`issuerId`, and the
 * project's percent of it in `shareE6`) — the last three for a person's pay on a project (V169).
 */
export const capturePriceSchema = z.object({
  format: z.enum(['hourly', 'billed_hourly', 'monthly', 'scope', 'revenue_share', 'profit_share', 'salary']),
  rateE4: positiveInt.nullable().default(null),
  amountMinor: positiveInt.nullable().default(null),
  shareE6: positiveInt.max(MAX_SHARE_E6, 'Процент — до 100').nullable().default(null),
  issuerId: id.nullable().default(null),
  currency,
});
export type CapturePrice = z.infer<typeof capturePriceSchema>;

const joinEvent = z.object({
  type: z.literal('join'),
  person: id.nullable().default(null),
  project: id.nullable().default(null),
  from: isoDate,
  pay: capturePriceSchema.nullable().default(null),
  planMinutes: positiveInt.nullable().default(null),
});

const leaveEvent = z.object({
  type: z.literal('leave'),
  assignment: id.nullable().default(null),
  to: isoDate,
});

const projectEvent = z.object({
  type: z.literal('project'),
  name: z.string().trim().default(''),
  client: id.nullable().default(null),
  from: isoDate,
  billing: z.enum(['hourly', 'fixed']).default('hourly'),
  price: capturePriceSchema.nullable().default(null),
  issuer: id.nullable().default(null),
  contract: id.nullable().default(null),
  person: id.nullable().default(null),
});

/**
 * `p:<projectId>` client price, `a:<assignmentId>` own pay (V245: no person terms),
 * `e:<issuerId>` the оклад of `person` through our entity (V259: `price` is `monthly`, its
 * `amountMinor` the gross per month). `creditEntities` (V255, own pay only): the entities whose
 * salary is credited against this project's pay (V145) — the full wanted set; null = leave the
 * credit as it is.
 */
const rateEvent = z.object({
  type: z.literal('rate'),
  target: z
    .string()
    .regex(/^[pae]:.+$/)
    .nullable()
    .default(null),
  person: id.nullable().default(null),
  price: capturePriceSchema.nullable().default(null),
  from: isoDate,
  creditEntities: z.array(id).max(50).nullable().default(null),
});

/**
 * «Новый человек / условия» (V202, V245): who, contractor or employee and the entity they are employed
 * in, and optionally a project with their rate on it from `from`.
 */
const personEvent = z.object({
  type: z.literal('person'),
  person: id.nullable().default(null),
  kind: z.enum(['contractor', 'employee']).default('contractor'),
  entity: id.nullable().default(null),
  from: isoDate.nullable().default(null),
  project: id.nullable().default(null),
  pay: capturePriceSchema.nullable().default(null),
});

/** «Данные человека» (V265, V266): the name and the location of an existing person. */
const editEvent = z.object({
  type: z.literal('edit'),
  person: id.nullable().default(null),
  name: z.string().trim().default(''),
  location: z.string().trim().nullable().default(null),
});

const statusEvent = z.object({
  type: z.literal('status'),
  project: id.nullable().default(null),
  status: z.enum(['paused', 'ended', 'active']).default('paused'),
  date: isoDate,
});

/** «Клиент оплатил»: a payment for one open document of the client (V187), in its currency. */
const paymentEvent = z.object({
  type: z.literal('payment'),
  client: id.nullable().default(null),
  invoice: id.nullable().default(null),
  amountMinor: positiveInt.nullable().default(null),
  currency,
  feeMinor: z.number().int().min(0).default(0),
  date: isoDate,
});

/** `ref` is `person:<id>`, `project:<id>` or `client:<id>`. */
const noteEvent = z.object({
  type: z.literal('note'),
  text: z.string().trim().default(''),
  ref: z
    .string()
    .regex(/^(person|project|client):.+$/)
    .nullable()
    .default(null),
});

/** «Увольнение из юрлица» (V178, V292): the person leaves the entity after the last working day (V175), with or without severance. */
const dismissEvent = z.object({
  type: z.literal('dismiss'),
  person: id.nullable().default(null),
  entity: id.nullable().default(null),
  lastDay: isoDate,
  severance: z.enum(SEVERANCE_OPTIONS).default('none'),
});

/**
 * «Оформление в юрлицо» (V203): the person employed in our entity from a date, with the salary paid
 * through it (on hand per month, the advance part); a salary of 0 ends it.
 */
const employEvent = z.object({
  type: z.literal('employ'),
  person: id.nullable().default(null),
  entity: id.nullable().default(null),
  netMinor: z.number().int().min(0).nullable().default(null),
  advanceMinor: z.number().int().min(0).default(0),
  currency: currency.default('USD'),
  from: isoDate,
});

/**
 * «Отпуск / за свой счёт» (V233, V234): a vacation or unpaid leave of a person from the first to the
 * last day, on the projects (`assignments`: their plan loses the leave's workdays) and the entities
 * picked — one paying the person a salary gets the event of the month the leave starts in (V72).
 */
export const ABSENCE_KINDS = ['vacation', 'unpaid'] as const;
const absenceEvent = z
  .object({
    type: z.literal('absence'),
    person: id.nullable().default(null),
    kind: z.enum(ABSENCE_KINDS).default('vacation'),
    from: isoDate,
    to: isoDate,
    assignments: z.array(id).default([]),
    entities: z.array(id).default([]),
  })
  .refine((e) => e.from <= e.to, { message: 'Последний день раньше первого', path: ['to'] });

/**
 * «Премия / денежная помощь» (V287): a one-off amount on hand through the entity the person is paid a
 * salary by — a bonus (taxed like the salary, averaged into vacation and sick pay) or the aid to a
 * молодой специалист (no income tax, not averaged). It is the salary event of the date's month.
 */
export const EXTRA_KINDS = ['bonus', 'aid'] as const;
export type ExtraKind = (typeof EXTRA_KINDS)[number];
const extraEvent = z.object({
  type: z.literal('extra'),
  person: id.nullable().default(null),
  entity: id.nullable().default(null),
  kind: z.enum(EXTRA_KINDS).default('bonus'),
  amountMinor: positiveInt.nullable().default(null),
  date: isoDate,
});

/** «Новый клиент» (V181): a client added with a form, as a project is. */
const clientEvent = z.object({
  type: z.literal('client'),
  name: z.string().trim().default(''),
  codeName: z.string().trim().max(200).nullable().default(null),
  country: z.string().trim().max(100).nullable().default(null),
  currency: currency.default('USD'),
});

export const captureEventSchema = z.discriminatedUnion('type', [
  absenceEvent,
  clientEvent,
  dismissEvent,
  employEvent,
  extraEvent,
  joinEvent,
  leaveEvent,
  projectEvent,
  rateEvent,
  personEvent,
  editEvent,
  statusEvent,
  paymentEvent,
  noteEvent,
]);
export type CaptureEvent = z.infer<typeof captureEventSchema>;
export type CaptureEventInput = z.input<typeof captureEventSchema>;
export type CaptureEventOf<T extends CaptureType> = Extract<CaptureEvent, { type: T }>;

export const captureBodySchema = z.object({ event: captureEventSchema });

/** auto = «подставлено», gap = «не хватает», link = «дальше», new = «новое»; null = the record. */
export type CaptureLineKind = 'auto' | 'gap' | 'link' | 'new';

export interface CaptureLine {
  readonly text: string;
  readonly kind: CaptureLineKind | null;
}

export interface CaptureFocus {
  readonly type: 'person' | 'project' | 'client';
  readonly id: string;
}

/** POST /api/v1/capture/preview — `ready: false` while a required field is missing. */
export interface CapturePreviewResponse {
  readonly ready: boolean;
  readonly lines: readonly CaptureLine[];
  readonly newGaps: readonly Gap[];
}

/** POST /api/v1/capture */
export interface CaptureResponse {
  readonly lines: readonly CaptureLine[];
  readonly focus: CaptureFocus | null;
  readonly newGaps: readonly Gap[];
  readonly undoToken: string;
  /** Toast text: the first line. */
  readonly message: string;
}

// ------------------------------------------------------------------ lookups for the dialog and gap fixes

export interface LookupPerson {
  readonly id: string;
  readonly name: string;
  readonly role: string | null;
  readonly status: 'candidate' | 'onboarding' | 'active' | 'offboarding' | 'left';
  readonly employmentType: 'contractor' | 'employee';
  /** The entity the person is on (V178: «Увольнение из юрлица» starts from it). */
  readonly employerId: string | null;
  /** Entities the person is employed in (V202): the only ones «через юрлицо» offers. */
  readonly entityIds: readonly string[];
  /** Entities paying the person a salary this month — «на ставке» on a leave (V234). */
  readonly salaryEntityIds: readonly string[];
  /** The salary through each entity in force today, else the next one (V238): read-only on a project. */
  readonly salaries: readonly LookupSalary[];
  /** Currency of the person's latest own rate on a project; null without one (V245). */
  readonly payCurrency: string | null;
  /** The location (V265): what «Данные человека» opens with. */
  readonly location: string | null;
}

/** A person's salary through one entity (V238): the оклад of a contract, or typed on hand. */
export interface LookupSalary {
  readonly issuerId: string;
  readonly from: string;
  readonly grossMinor: number | null;
  readonly netMinor: number;
  readonly currency: string;
  /** «№ 3» of the contract; null = typed on «Оформление». */
  readonly contractNumber: string | null;
}

export interface LookupProject {
  readonly id: string;
  readonly name: string;
  readonly clientId: string | null;
  readonly clientName: string | null;
  readonly status: 'active' | 'paused' | 'ended';
  readonly billingMode: 'hourly' | 'fixed' | 'scope' | 'payroll' | 'internal';
}

export interface LookupClient {
  readonly id: string;
  readonly name: string;
  readonly country: string | null;
  readonly currency: string;
}

/** Active (not terminated, not ended) client contracts. */
export interface LookupContract {
  readonly id: string;
  readonly number: string;
  readonly clientId: string;
}

/** Assignments still running today (no end, or ending today or later). */
/** The person's pay on a project this month (V255): what «Изменение ставки человека» opens with. */
export interface LookupPay {
  readonly format: PayFormat;
  readonly rateE4: number | null;
  readonly amountMinor: number | null;
  readonly shareE6: number | null;
  readonly issuerId: string | null;
  readonly currency: string;
}

export interface LookupAssignment {
  readonly id: string;
  readonly personId: string;
  readonly projectId: string;
  /** «Project · Client». */
  readonly label: string;
  /** «Client-or-project 26 $/ч» with « (своя)» for an own rate; null without a rate. */
  readonly rateLabel: string | null;
  /** Pay format this month (the rate form's default). */
  readonly payFormat: PayFormat | null;
  /** The pay this month in full (V255); null without a rate. */
  readonly pay: LookupPay | null;
  /** Entities whose salary is credited against this project's pay (V145) — ticked in the rate form (V255). */
  readonly creditEntityIds: readonly string[];
  /** Last day of an ended assignment with unpaid months, which only the rate form offers (V182); null while it runs. */
  readonly endedOn: string | null;
  /**
   * Entities whose salary this month pays for the project (V235): the person's salary credited against
   * it (V145), or the project paid in «Зарплата через юрлицо» through the entity (V69).
   */
  readonly salaryEntityIds: readonly string[];
}

export interface LookupEntity {
  readonly id: string;
  readonly name: string;
  readonly isEmployment: boolean;
}

/** A document of the client still open for payments (V187), oldest first. */
export interface LookupInvoice {
  readonly id: string;
  readonly clientId: string;
  /** «1054-2026 · авг 2026». */
  readonly label: string;
  readonly currency: string;
  /** What is still due (amount + fee may settle it). */
  readonly openMinor: number;
}

/** GET /api/v1/capture/lookups */
export interface CaptureLookups {
  readonly today: string;
  readonly people: readonly LookupPerson[];
  readonly projects: readonly LookupProject[];
  readonly clients: readonly LookupClient[];
  readonly contracts: readonly LookupContract[];
  readonly assignments: readonly LookupAssignment[];
  /** Live, not archived entities. */
  readonly entities: readonly LookupEntity[];
  readonly invoices: readonly LookupInvoice[];
}

// ------------------------------------------------------------------ gap fixes

/** Hours a gap fix accepts: 0–744 h (design GapFix). */
export const GAP_HOURS_MAX_MINUTES = 744 * 60;
/** IBAN length checks of the design GapFix. */
export const PERSON_IBAN_MIN = 12;
export const ENTITY_IBAN_MIN = 15;

const minutes = z.number().int().min(0).max(GAP_HOURS_MAX_MINUTES);
const basisPoints = z.number().int().min(0).max(10_000);

/** The `value` each fix kind accepts in POST /gaps/:id/resolve. */
export const gapValueSchemas = {
  client: id,
  issuer: id,
  contract: z.union([id, z.object({ number: z.string().trim().min(1) })]),
  projectPrice: z.object({
    billing: z.enum(['hourly', 'fixed']),
    rateE4: positiveInt.nullable().default(null),
    amountMinor: positiveInt.nullable().default(null),
    currency,
    from: isoDate,
  }),
  assignmentPay: z.object({
    format: z.enum(['hourly', 'monthly']),
    rateE4: positiveInt.nullable().default(null),
    amountMinor: positiveInt.nullable().default(null),
    currency,
    from: isoDate,
  }),
  bank: z
    .string()
    .trim()
    .refine((v) => v.replace(/\s/g, '').length >= PERSON_IBAN_MIN, 'IBAN выглядит коротким'),
  personEntity: id,
  contractorContract: z.literal(true),
  entityField: z.string().trim().min(1, 'Заполните поле'),
  entityTaxes: z.object({ incomeBp: basisPoints, pensionBp: basisPoints, employerBp: basisPoints }),
  entityBank: z
    .string()
    .transform((v) => v.replace(/\s+/g, '').toUpperCase())
    .refine((v) => v.length >= ENTITY_IBAN_MIN, 'Проверьте IBAN'),
  hours: minutes,
  billHours: minutes,
  reissue: z.literal(true),
  email: z
    .string()
    .trim()
    .regex(/^[^@\s]+@[^@\s]+\.[^@\s]+$/, 'Проверьте адрес'),
  renew: z.enum(['extend', 'end']),
  done: z.literal(true),
} as const;

export type GapFixKind = keyof typeof gapValueSchemas;
export type GapValue<K extends GapFixKind> = z.infer<(typeof gapValueSchemas)[K]>;

export const gapResolveBodySchema = z.object({ value: z.unknown() });

/** POST /api/v1/gaps/:gapId/resolve */
export interface GapResolveResponse {
  readonly message: string;
  readonly undoToken: string;
}
